const isRedirectingKey = 'isRedirecting'

// BSSERP-2124: the `isRedirecting` guard (BSSERP-2089) prevents concurrent Okta
// redirect flows from clobbering each other's PKCE code_verifier, but nothing
// ever cleared it if the tab was closed/crashed/navigated away mid-redirect —
// leaving every subsequent re-auth attempt silently no-op'd forever. Stamping
// the flag with the time it was set lets us treat it as expired (and clear it)
// after this TTL, without weakening the guard's protection against genuine
// near-simultaneous redirects.
export const REDIRECT_GUARD_TTL_MS = 20000

interface RedirectGuardState {
  value: boolean
  timestamp: number
}

const localStorageService = {
  setIsOktaRedirecting: (valueToSet: boolean) => {
    const state: RedirectGuardState = { value: valueToSet, timestamp: Date.now() }
    localStorage.setItem(isRedirectingKey, JSON.stringify(state))
  },
  isOktaRedirecting: (): boolean => {
    const rawState = localStorage.getItem(isRedirectingKey)
    if (!rawState) {
      return false
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(rawState)
    } catch {
      parsed = undefined
    }

    const isValidState =
      typeof parsed === 'object' && parsed !== null && typeof (parsed as RedirectGuardState).timestamp === 'number'

    if (!isValidState) {
      // Corrupt or pre-BSSERP-2124 legacy value (bare 'true'/'false' string) —
      // clear it rather than let it wedge the guard forever with no TTL to save it.
      localStorage.removeItem(isRedirectingKey)
      return false
    }

    const state = parsed as RedirectGuardState

    if (!state.value) {
      return false
    }

    const hasExpired = Date.now() - state.timestamp > REDIRECT_GUARD_TTL_MS
    if (hasExpired) {
      localStorage.removeItem(isRedirectingKey)
      return false
    }

    return true
  },
  setValueForKey: (key: string, value: string): string | null => {
    const previousValue = localStorage.getItem(key)
    localStorage.setItem(key, value)
    return previousValue
  },
  valueForKeyExists: (key: string): boolean => localStorage.getItem(key) !== null,
}

export default localStorageService
